import { useId, useRef, useState } from 'react';

import { cn } from '../utils/cn';

export interface MarkdownEditorProps {
  /**
   * Editor label
   */
  label?: string;

  /**
   * Current value
   */
  value: string;

  /**
   * Change handler
   */
  onChange: (value: string) => void;

  /**
   * Placeholder text
   */
  placeholder?: string;

  /**
   * Number of rows
   * @default 6
   */
  rows?: number;

  /**
   * Error message
   */
  error?: string;

  /**
   * Helper text
   */
  helperText?: string;

  /**
   * Whether the field is required
   */
  required?: boolean;

  /**
   * Disabled state
   */
  disabled?: boolean;

  /**
   * Show preview tab
   * @default true
   */
  showPreview?: boolean;
}

type MarkdownAction =
  | 'bold'
  | 'italic'
  | 'heading'
  | 'bullet'
  | 'number'
  | 'quote'
  | 'code'
  | 'link';

/**
 * MarkdownEditor Component
 *
 * A text editor with markdown formatting toolbar
 */
export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  label,
  value,
  onChange,
  placeholder,
  rows = 6,
  error,
  helperText,
  required = false,
  disabled = false,
  showPreview = true,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [activeTab, setActiveTab] = useState<'write' | 'preview'>('write');

  const insertMarkdown = (action: MarkdownAction) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const beforeText = value.substring(0, start);
    const afterText = value.substring(end);

    let newText = '';
    let cursorOffset = 0;

    switch (action) {
      case 'bold':
        newText = `${beforeText}**${selectedText || 'bold text'}**${afterText}`;
        cursorOffset = selectedText ? 2 : 11;
        break;
      case 'italic':
        newText = `${beforeText}_${selectedText || 'italic text'}_${afterText}`;
        cursorOffset = selectedText ? 1 : 12;
        break;
      case 'heading': {
        const lineStart = value.lastIndexOf('\n', start - 1) + 1;
        const lineEnd = value.indexOf('\n', start);
        const line = value.substring(lineStart, lineEnd === -1 ? value.length : lineEnd);
        const beforeLine = value.substring(0, lineStart);
        const afterLine = value.substring(lineEnd === -1 ? value.length : lineEnd);
        newText = `${beforeLine}## ${line}${afterLine}`;
        cursorOffset = 3;
        break;
      }
      case 'bullet':
        newText = `${beforeText}\n- ${selectedText || 'List item'}${afterText}`;
        cursorOffset = selectedText ? 3 : 13;
        break;
      case 'number':
        newText = `${beforeText}\n1. ${selectedText || 'List item'}${afterText}`;
        cursorOffset = selectedText ? 4 : 14;
        break;
      case 'quote':
        newText = `${beforeText}\n> ${selectedText || 'Quote'}${afterText}`;
        cursorOffset = selectedText ? 3 : 9;
        break;
      case 'code':
        if (selectedText.includes('\n')) {
          newText = `${beforeText}\`\`\`\n${selectedText || 'code'}\n\`\`\`${afterText}`;
          cursorOffset = 4;
        } else {
          newText = `${beforeText}\`${selectedText || 'code'}\`${afterText}`;
          cursorOffset = selectedText ? 1 : 5;
        }
        break;
      case 'link':
        newText = `${beforeText}[${selectedText || 'link text'}](url)${afterText}`;
        cursorOffset = selectedText ? selectedText.length + 3 : 13;
        break;
    }

    onChange(newText);

    // Set cursor position after state update
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + cursorOffset;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const renderPreview = () => {
    // Simple markdown to HTML conversion for preview
    let html = value
      // Headings
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold mt-4 mb-2">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-4 mb-2">$1</h1>')
      // Bold
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
      // Italic
      .replace(/_(.*?)_/g, '<em class="italic">$1</em>')
      // Links
      .replace(
        /\[(.*?)\]\((.*?)\)/g,
        '<a href="$2" class="text-primary-600 hover:underline">$1</a>'
      )
      // Code blocks
      .replace(
        /```([\s\S]*?)```/g,
        '<pre class="bg-gray-100 p-2 rounded my-2 overflow-x-auto"><code>$1</code></pre>'
      )
      // Inline code
      .replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1 rounded">$1</code>')
      // Quotes
      .replace(
        /^> (.*$)/gim,
        '<blockquote class="border-l-4 border-gray-300 pl-4 italic my-2">$1</blockquote>'
      )
      // Unordered lists
      .replace(/^- (.*$)/gim, '<li class="ml-4">$1</li>')
      // Ordered lists
      .replace(/^\d+\. (.*$)/gim, '<li class="ml-4 list-decimal">$1</li>')
      // Line breaks
      .replace(/\n/g, '<br />');

    // Wrap list items
    html = html.replace(/(<li class="ml-4">.*?<\/li>)/g, '<ul class="list-disc">$1</ul>');
    html = html.replace(
      /(<li class="ml-4 list-decimal">.*?<\/li>)/g,
      '<ol class="list-decimal">$1</ol>'
    );

    return html;
  };

  const editorId = `editor-${useId()}`;
  const errorId = `${editorId}-error`;
  const helperTextId = `${editorId}-helper`;

  return (
    <div className="space-y-2">
      {label && (
        <label
          htmlFor={editorId}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {label}
          {required && <span className="text-error-500 dark:text-error-400 ml-1">*</span>}
        </label>
      )}

      <div
        className={cn(
          'border rounded-lg overflow-hidden bg-white dark:bg-gray-800',
          error ? 'border-error-500' : 'border-gray-300 dark:border-gray-600',
          disabled && 'bg-gray-50 dark:bg-gray-900 opacity-60'
        )}
      >
        {/* Tabs */}
        {showPreview && (
          <div className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <button
              type="button"
              onClick={() => setActiveTab('write')}
              disabled={disabled}
              className={cn(
                'px-4 py-2 text-sm font-medium',
                activeTab === 'write'
                  ? 'bg-white dark:bg-gray-800 border-b-2 border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              )}
            >
              Write
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('preview')}
              disabled={disabled}
              className={cn(
                'px-4 py-2 text-sm font-medium',
                activeTab === 'preview'
                  ? 'bg-white dark:bg-gray-800 border-b-2 border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              )}
            >
              Preview
            </button>
          </div>
        )}

        {/* Toolbar */}
        {activeTab === 'write' && (
          <div className="flex flex-wrap gap-1 p-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
            <ToolbarButton
              onClick={() => insertMarkdown('bold')}
              title="Bold (Ctrl+B)"
              disabled={disabled}
            >
              <strong>B</strong>
            </ToolbarButton>
            <ToolbarButton
              onClick={() => insertMarkdown('italic')}
              title="Italic (Ctrl+I)"
              disabled={disabled}
            >
              <em>I</em>
            </ToolbarButton>
            <ToolbarButton
              onClick={() => insertMarkdown('heading')}
              title="Heading"
              disabled={disabled}
            >
              H
            </ToolbarButton>
            <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1" />
            <ToolbarButton
              onClick={() => insertMarkdown('bullet')}
              title="Bullet List"
              disabled={disabled}
            >
              • List
            </ToolbarButton>
            <ToolbarButton
              onClick={() => insertMarkdown('number')}
              title="Numbered List"
              disabled={disabled}
            >
              1. List
            </ToolbarButton>
            <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1" />
            <ToolbarButton
              onClick={() => insertMarkdown('quote')}
              title="Quote"
              disabled={disabled}
            >
              &ldquo;&rdquo;
            </ToolbarButton>
            <ToolbarButton onClick={() => insertMarkdown('code')} title="Code" disabled={disabled}>
              &lt;/&gt;
            </ToolbarButton>
            <ToolbarButton onClick={() => insertMarkdown('link')} title="Link" disabled={disabled}>
              Link
            </ToolbarButton>
          </div>
        )}

        {/* Editor/Preview */}
        {activeTab === 'write' ? (
          <textarea
            ref={textareaRef}
            id={editorId}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={rows}
            disabled={disabled}
            required={required}
            className={cn(
              'w-full px-4 py-3 text-base resize-y',
              'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
              'placeholder:text-gray-400 dark:placeholder:text-gray-500',
              'focus:outline-none focus:ring-2 focus:ring-primary-500',
              'disabled:bg-gray-50 dark:disabled:bg-gray-900 disabled:text-gray-500 dark:disabled:text-gray-600',
              error && 'text-error-900 dark:text-error-200'
            )}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? errorId : helperText ? helperTextId : undefined}
          />
        ) : (
          <div
            className="px-4 py-3 prose prose-sm dark:prose-invert max-w-none min-h-[150px] text-gray-900 dark:text-gray-100"
            dangerouslySetInnerHTML={{ __html: renderPreview() }}
          />
        )}
      </div>

      {error && (
        <p id={errorId} className="text-sm text-error-600 dark:text-error-400" role="alert">
          {error}
        </p>
      )}

      {!error && helperText && (
        <p id={helperTextId} className="text-sm text-gray-500 dark:text-gray-400">
          {helperText}
        </p>
      )}
    </div>
  );
};

interface ToolbarButtonProps {
  onClick: () => void;
  title: string;
  disabled?: boolean;
  children: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ onClick, title, disabled, children }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={cn(
        'px-2 py-1 text-sm rounded text-gray-700 dark:text-gray-300',
        'hover:bg-gray-200 dark:hover:bg-gray-700',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'transition-colors'
      )}
    >
      {children}
    </button>
  );
};

MarkdownEditor.displayName = 'MarkdownEditor';
